import flatten from 'lodash/flatten'
import get from 'lodash/get'
import omit from 'lodash/omit'
import type {
  Contact,
  ContactId,
  ContactNote,
  RecipientListId,
} from 'types/list'
import type { GetState, Thunk } from 'types/redux'
import type { Pro, Publisher } from 'types/writer'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  addPro,
  addPublishers,
} from '@disco/state-management/actions/rights-data-actions'
import api from '@disco/api/server'
import {
  CONTACT_ACTIVITY_LOAD,
  CONTACT_ACTIVITY_LOAD_FAIL,
  CONTACT_ACTIVITY_LOAD_SUCCESS,
  CONTACT_NOTES_CREATE,
  CONTACT_NOTES_CREATE_FAIL,
  CONTACT_NOTES_CREATE_SUCCESS,
  CONTACT_NOTES_DELETE,
  CONTACT_NOTES_DELETE_FAIL,
  CONTACT_NOTES_DELETE_SUCCESS,
  CONTACT_NOTES_LOAD,
  CONTACT_NOTES_LOAD_FAIL,
  CONTACT_NOTES_LOAD_SUCCESS,
  CONTACT_NOTES_UPDATE,
  CONTACT_NOTES_UPDATE_FAIL,
  CONTACT_NOTES_UPDATE_SUCCESS,
  CONTACTS_ADD_LIST,
  CONTACTS_ADD_LIST_FAIL,
  CONTACTS_ADD_LIST_SUCCESS,
  CONTACTS_ADD_WARNING,
  CONTACTS_CANCEL_EDITING,
  CONTACTS_CREATE,
  CONTACTS_CREATE_FAIL,
  CONTACTS_CREATE_SUCCESS,
  CONTACTS_DELETE,
  CONTACTS_DELETE_FAIL,
  CONTACTS_DELETE_SUCCESS,
  CONTACTS_IMPORT,
  CONTACTS_IMPORT_FAIL,
  CONTACTS_IMPORT_SUCCESS,
  CONTACTS_LOAD,
  CONTACTS_LOAD_ALL,
  CONTACTS_LOAD_ALL_FAIL,
  CONTACTS_LOAD_ALL_SUCCESS,
  CONTACTS_LOAD_FAIL,
  CONTACTS_LOAD_IDS,
  CONTACTS_LOAD_IDS_FAIL,
  CONTACTS_LOAD_IDS_NEXT_SUCCESS,
  CONTACTS_LOAD_IDS_SUCCESS,
  CONTACTS_LOAD_SUCCESS,
  CONTACTS_MODE_SWITCH,
  CONTACTS_REMOVE_LIST,
  CONTACTS_REMOVE_LIST_FAIL,
  CONTACTS_REMOVE_LIST_SUCCESS,
  CONTACTS_REMOVE_WARNING,
  CONTACTS_SEARCH,
  CONTACTS_SET_COLUMN_MAPPING,
  CONTACTS_SORT,
  CONTACTS_TOGGLE,
  CONTACTS_UNSUBSCRIBE_FAIL,
  CONTACTS_UNSUBSCRIBE_SUCCESS,
  CONTACTS_UPDATE,
  CONTACTS_UPDATE_FAIL,
  CONTACTS_UPDATE_SUCCESS,
  CONTACTS_UPDATE_TAGS,
  CONTACTS_UPDATE_TAGS_FAIL,
  CONTACTS_UPDATE_TAGS_SUCCESS,
  CONTACTS_VALUE_ADD,
  CONTACTS_VALUE_CHANGE,
  CONTACTS_VALUE_REMOVE,
  PRO_ADD_SUCCESS,
  PRO_UPDATE_SUCCESS,
  PUBLISHERS_ADD_SUCCESS,
  PUBLISHERS_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import {
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
} from '@disco/constants/SortTypes'
import {
  getContactsArray,
  getContactsIds,
} from '@disco/state-management/selectors/contacts'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import { maybeAddPeriod } from '@disco/utils/string-utils'
import { areTagsIncludeValues } from '@disco/utils/tags'
/**
 * Loads contacts by their IDs from the server.
 * @param ids - Array of contact IDs to load
 * @returns Thunk that loads contacts and handles success/error states
 */
export const loadContactsByIds = (
  ids: number[],
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      dispatch({
        type: CONTACTS_LOAD,
      })
      const { results } = await api.getContactsByIds(ids)
      dispatch({
        type: CONTACTS_LOAD_SUCCESS,
        contacts: results,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_LOAD_FAIL,
        error,
      })
      console.error(error)
      throw new Error('Writers not found when fetching contacts.')
    }
  }
}
/**
 * Loads next batch of contacts within a specified index range.
 * @param fromIndex - Starting index for loading contacts
 * @param toIndex - Ending index for loading contacts
 * @returns Thunk that loads contacts not already loaded in the specified range
 */
export const loadNextContacts = (
  fromIndex: number,
  toIndex: number,
): ((dispatch: any, getState: GetState) => void) => {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const state = getState()
    const contactsIds = getContactsIds(state)
    const contacts = getContactsArray(state)
    const idsInterval = contactsIds.slice(fromIndex, toIndex)
    const loadedContactsIds = contacts
      .filter(({ id }) => idsInterval.includes(id))
      .map(contact => contact.id)
    const idsToLoad = idsInterval.filter(id => !loadedContactsIds.includes(id))
    if (!idsToLoad.length) return
    dispatch(loadContactsByIds(idsToLoad))
  }
}
// TODO: Deprecate loadAllContacts https://disco-team.atlassian.net/browse/DISCO-4415
/**
 * Loads all contacts from the server by paginating through all pages.
 * @param url - Optional pagination URL to continue loading from
 * @param loadedContacts - Array of already loaded contacts to accumulate results
 * @returns Thunk that recursively loads all contacts across multiple pages
 */
export const loadAllContacts = (
  url?: string,
  loadedContacts: Contact[] = [],
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    if (!loadedContacts?.length) {
      dispatch({
        type: CONTACTS_LOAD_ALL,
      })
    }

    try {
      let contacts

      if (url) {
        contacts = await api.getUrl(url, null, 'api.getContacts')
      } else {
        contacts = await api.getContacts(undefined, undefined, undefined, 1000)
      }

      if (contacts?.next) {
        dispatch(
          loadAllContacts(contacts.next, [
            ...loadedContacts,
            ...(contacts?.results ?? []),
          ]),
        )
        return
      }

      dispatch({
        type: CONTACTS_LOAD_ALL_SUCCESS,
        contacts: [...loadedContacts, ...(contacts?.results ?? [])],
        next: contacts?.next,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_LOAD_ALL_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads contact IDs with optional filtering and sorting.
 * @param query - Optional search query to filter contacts
 * @param sort - Optional sort parameter for ordering
 * @param recipientId - Optional recipient ID for filtering
 * @returns Thunk that loads contact IDs and handles pagination
 */
export const loadContactsIds = (
  query?: string,
  sort?: string,
  recipientId?: number,
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACTS_LOAD_IDS,
    })

    try {
      const response = await api.getContactsIds(query, sort, recipientId)

      // when paginated, keep the previous ids until the full list is
      // accumulated — replacing them with a partial page breaks anything
      // relying on complete ids (e.g. an active select-all)
      if (response.next) {
        dispatch(loadNextContactsIds(response.next, response.results))
        return
      }

      dispatch({
        type: CONTACTS_LOAD_IDS_SUCCESS,
        ids: response.results,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_LOAD_IDS_FAIL,
        error,
      })
    }
  }
}
/**
 * Loads next page of contact IDs using pagination.
 * @param url - Pagination URL for the next page of contact IDs
 * @param loadedIds - Array of already loaded contact IDs to accumulate results
 * @returns Thunk that recursively loads contact IDs across multiple pages
 */
export const loadNextContactsIds = (
  url: string,
  loadedIds: number[] = [],
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    if (!loadedIds?.length) {
      dispatch({
        type: CONTACTS_LOAD_IDS,
      })
    }

    try {
      const response = await api.getUrl<any>(
        url,
        null,
        'api.getNextContactsIds',
      )

      if (response.next) {
        dispatch(
          loadNextContactsIds(response.next, [
            ...loadedIds,
            ...response.results,
          ]),
        )
        return
      }

      dispatch({
        type: CONTACTS_LOAD_IDS_NEXT_SUCCESS,
        ids: [...loadedIds, ...response.results],
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_LOAD_IDS_FAIL,
        error,
      })
    }
  }
}
/**
 * Adds a new contact with optional list assignment.
 * @param contact - Contact object to add
 * @param listId - Optional recipient list ID to assign contact to
 * @returns Thunk that creates contact with publisher/pro sync and returns the added contact
 */
export const addContact = (
  contact: Contact,
  listId?: RecipientListId,
): ((dispatch: any, getState: GetState) => Promise<Contact>) => {
  return async (
    dispatch: (...args: Array<any>) => any,
    getState: GetState,
  ): Promise<Contact> => {
    dispatch({
      type: CONTACTS_CREATE,
    })
    const contacts = [contact]

    try {
      const newPublishers = getNewPublishers(
        contacts,
        getState().entities.publishers,
      )
      const newPros = getNewPros(contacts, getState().entities.pro)
      // sync call modifies pro and publisher objects in reducers, get
      // them after call
      await dispatch(syncContactPublishersAndPro(newPublishers, newPros))
      const {
        entities: { pro, publishers },
      } = getState()
      const modifiedContacts = fixContactPendingChanges(
        contacts,
        pro,
        publishers,
      ).map(c => transformPendingContact(c))
      const result = await api.importContacts(modifiedContacts, false, listId)
      const addedContact = result.contacts[0]
      dispatch({
        type: CONTACTS_CREATE_SUCCESS,
        contact: addedContact,
        listId: listId,
      })
      pushUINotification({
        message: 'Contact added!',
        level: 'success',
      })
      return addedContact
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: CONTACTS_CREATE_FAIL,
        error,
      })
      throw error
    }
  }
}
/**
 * Imports multiple contacts with optional list assignment.
 * @param contacts - Array of contacts to import
 * @param listId - Optional recipient list ID to assign contacts to
 * @returns Thunk that imports contacts and handles deferred processing
 */
export const importContacts = (
  contacts: Contact[],
  listId: RecipientListId | null | undefined, // TODO: update type
): any => {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACTS_IMPORT,
      contacts,
    })

    try {
      const result = await api.importContacts(contacts, true, listId)

      if (!result.deferred) {
        dispatch({
          type: CONTACTS_IMPORT_SUCCESS,
          contacts: result.contacts,
          listId: listId,
          meta: {
            mixpanel: {
              event: 'Import contacts (csv)',
            },
          },
        })
      }

      return result
    } catch (error) {
      dispatch({
        type: CONTACTS_IMPORT_FAIL,
        contacts,
        error,
      })
      throw error
    }
  }
}
/**
 * Adds a note to a specific contact.
 * @param contactId - ID of the contact to add note to
 * @param note - Note content to add
 * @returns Thunk that creates contact note and tracks analytics
 */
export const addContactNote = (
  contactId: ContactId,
  note: ContactNote,
): ((dispatch: any) => Promise<unknown>) => {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACT_NOTES_CREATE,
      contactId,
    })
    return api
      .addContactNote(contactId, note)
      .then(result =>
        dispatch({
          type: CONTACT_NOTES_CREATE_SUCCESS,
          contactId,
          note: result,
          meta: {
            mixpanel: {
              event: 'Add contact note',
            },
          },
        }),
      )
      .catch(error =>
        dispatch({
          type: CONTACT_NOTES_CREATE_FAIL,
          contactId,
          error,
        }),
      )
  }
}
/**
 * Deletes a note from a specific contact.
 * @param contactId - ID of the contact to delete note from
 * @param noteId - ID of the note to delete
 * @returns Thunk that deletes contact note and tracks analytics
 */
export const deleteContactNote = (
  contactId: ContactId,
  noteId: number,
): ((dispatch: any) => Promise<unknown>) => {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACT_NOTES_DELETE,
      contactId,
      noteId,
    })
    return api
      .deleteContactNote(noteId)
      .then(() =>
        dispatch({
          type: CONTACT_NOTES_DELETE_SUCCESS,
          contactId,
          noteId,
          meta: {
            mixpanel: {
              event: 'Delete contact note',
            },
          },
        }),
      )
      .catch(error =>
        dispatch({
          type: CONTACT_NOTES_DELETE_FAIL,
          contactId,
          noteId,
          error,
        }),
      )
  }
}
/**
 * Updates a note for a specific contact.
 * @param contactId - ID of the contact to update note for
 * @param noteId - ID of the note to update
 * @param text - Updated note content
 * @returns Thunk that updates contact note and tracks analytics
 */
export const updateContactNote = (
  contactId: ContactId,
  noteId: number,
  text: ContactNote,
): ((dispatch: any) => Promise<unknown>) => {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACT_NOTES_UPDATE,
      contactId,
      noteId,
    })
    return api
      .updateContactNote(contactId, noteId, text)
      .then(result =>
        dispatch({
          type: CONTACT_NOTES_UPDATE_SUCCESS,
          contactId,
          note: result,
          meta: {
            mixpanel: {
              event: 'Update contact note',
            },
          },
        }),
      )
      .catch(error =>
        dispatch({
          type: CONTACT_NOTES_UPDATE_FAIL,
          contactId,
          noteId,
          error,
        }),
      )
  }
}
/**
 * Loads activity data for a specific contact.
 * @param contactId - The ID of the contact to load activity for
 * @returns Thunk that dispatches activity loading actions
 */
export const loadContactActivity = (
  contactId: ContactId,
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACT_ACTIVITY_LOAD,
      contactId,
    })

    try {
      const activity = await api.getContactActivity(contactId)
      return dispatch({
        type: CONTACT_ACTIVITY_LOAD_SUCCESS,
        contactId,
        activity,
      })
    } catch (error) {
      return dispatch({
        type: CONTACT_ACTIVITY_LOAD_FAIL,
        contactId,
        error,
      })
    }
  }
}
/**
 * Loads notes for a specific contact.
 * @param contactId - The ID of the contact to load notes for
 * @returns Thunk that dispatches notes loading actions
 */
export const loadContactNotes = (
  contactId: ContactId,
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACT_NOTES_LOAD,
      contactId,
    })

    try {
      const notes = await api.getContactNotes(contactId)
      return dispatch({
        type: CONTACT_NOTES_LOAD_SUCCESS,
        contactId,
        notes,
      })
    } catch (error) {
      return dispatch({
        type: CONTACT_NOTES_LOAD_FAIL,
        contactId,
        error,
      })
    }
  }
}
/**
 * Sorts contacts by a given parameter and direction.
 * @param newSortParam - The parameter to sort by
 * @returns Thunk that dispatches contact sorting actions
 */
export const sortContacts = (
  newSortParam: string,
): ((dispatch: any, getState: GetState) => any) => {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { sortParam, sortDirection } = getState().entities.contacts

    if (sortParam !== newSortParam) {
      return dispatch({
        type: CONTACTS_SORT,
        sortParam: newSortParam,
        sortDirection: SORT_DIRECTION_ASC,
      })
    }

    return dispatch({
      type: CONTACTS_SORT,
      sortParam: newSortParam,
      sortDirection:
        sortDirection === SORT_DIRECTION_DESC
          ? SORT_DIRECTION_ASC
          : SORT_DIRECTION_DESC,
    })
  }
}
/**
 * Resets contact sorting to default (Name, ascending).
 * @returns Thunk that resets contact sorting
 */
export const resetSortContacts = (): ((dispatch: any) => void) => {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACTS_SORT,
      sortParam: 'Name',
      sortDirection: SORT_DIRECTION_ASC,
    })
  }
}
/**
 * Adds contacts to a recipient list.
 * @param listId - ID of the recipient list to add contacts to
 * @param contacts - Array of contact objects to add
 * @param ids - Optional array of contact IDs to use instead of extracting from contacts
 * @returns Thunk that adds contacts to list and handles warnings
 */
export const addToList = (
  listId: RecipientListId,
  contacts: Array<Contact>,
  ids?: ContactId[],
): Thunk => {
  return async dispatch => {
    try {
      dispatch({
        type: CONTACTS_ADD_LIST,
        listId: listId,
        contacts,
        ids,
      })
      const { warnings } = await api.alterRecipients(
        listId,
        ids || contacts.map(c => c.id),
        [],
      )
      let warningContact: number | null = null

      if (warnings) {
        warningContact = warnings[0].contact_ids[0]
        if (warningContact !== null) {
          dispatch(loadContactsByIds([warningContact]))
          dispatch({
            type: CONTACTS_ADD_WARNING,
            warning: warningContact,
          })
        }
      }

      dispatch({
        type: CONTACTS_ADD_LIST_SUCCESS,
        listId: listId,
        contacts,
        ids,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_ADD_LIST_FAIL,
        error,
        listId: listId,
        contacts,
        ids,
      })
      throw error
    }
  }
}
/**
 * Removes contacts from a recipient list.
 * @param listId - ID of the recipient list to remove contacts from
 * @param contacts - Array of contact objects to remove
 * @param ids - Optional array of contact IDs to use instead of extracting from contacts
 * @returns Thunk that removes contacts from list
 */
export const removeFromList = (
  listId: RecipientListId,
  contacts: Array<Contact>,
  ids?: ContactId[],
): Thunk => {
  return async dispatch => {
    try {
      dispatch({
        type: CONTACTS_REMOVE_LIST,
        listId: listId,
        contacts,
      })
      await api.alterRecipients(listId, [], ids || contacts.map(c => c.id))
      dispatch({
        type: CONTACTS_REMOVE_LIST_SUCCESS,
        listId: listId,
        contacts,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_REMOVE_LIST_FAIL,
        error,
        listId: listId,
        contacts,
      })
      throw error
    }
  }
}
/**
 * Removes warning from contacts state.
 * @returns Action object to remove warning
 */
export const removeWarning = (): {
  type: string
} => ({
  type: CONTACTS_REMOVE_WARNING,
})
/**
 * Sets column mapping for contact imports.
 * @param columnMapping - Mapping configuration for contact fields
 * @param error - Optional error object if mapping failed
 * @returns Action object with column mapping and error state
 */
export const setColumnMapping = (
  columnMapping: Record<string, any> | null | undefined,
  error?: Error | null | undefined,
): {
  columnMapping: any | null | undefined
  error: Error | null | undefined
  type: string
} => ({
  type: CONTACTS_SET_COLUMN_MAPPING,
  columnMapping,
  error,
})
/**
 * Searches contacts by term and tracks analytics.
 * @param searchTerm - Search term to filter contacts
 * @returns Action object with search term and optional analytics metadata
 */
export const searchContacts = (
  searchTerm: string,
): {
  meta: {
    mixpanel: {
      event: string
    }
  } | void
  searchTerm: string
  type: string
} => ({
  type: CONTACTS_SEARCH,
  searchTerm,
  meta:
    searchTerm && searchTerm.length > 0
      ? {
          mixpanel: {
            event: 'Search contacts',
          },
        }
      : undefined,
})
/**
 * Switches contacts between normal and batch/edit modes.
 * @param ids - Array of contact IDs to switch modes for
 * @param batchMode - Whether to enable batch mode
 * @param switchToEditMode - Optional flag to switch to edit mode
 * @returns Thunk that handles mode switching with automatic update handling
 */
export const switchContactsMode = (
  ids: ContactId[],
  batchMode: boolean,
  switchToEditMode?: boolean,
): ((dispatch: any, getState: GetState) => any | void) => {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { contacts } = getState().entities.contacts
    const partOfEditedContacts = contacts.some(
      c => c.editMode && ids.includes(c.id),
    )

    if (!switchToEditMode && partOfEditedContacts) {
      const modifiedContacts = contacts.filter(c => c.editMode)

      if (modifiedContacts.length) {
        const changedContacts =
          modifiedContacts.length === 1
            ? modifiedContacts
            : modifiedContacts.map(c => omit(c, ['name', 'email']))
        dispatch(updateContacts(changedContacts)).catch(() => {})
        const eventName =
          modifiedContacts.length === 1
            ? 'Edit single contact in table'
            : 'Batch edit contacts in table'
        dispatch(sendMixpanelEvent(eventName))
        return
      }
    }

    return dispatch({
      type: CONTACTS_MODE_SWITCH,
      ids,
      batchMode,
      switchToEditMode,
    })
  }
}

/**
 * Gets new publishers from contacts that don't exist in current publisher list.
 * @param contacts - Array of contacts to check for new publishers
 * @param publishers - Existing publisher list to compare against
 * @returns Array of new publishers that need to be added
 */
const getNewPublishers = (
  contacts: Contact[],
  publishers: Publisher[],
): Publisher[] => {
  return flatten(
    contacts.map(c => get(c, 'pendingChanges.publishers', [])),
  ).filter(
    p =>
      !publishers.some(
        //TODO: review toString() comparision
        existingPublisher => existingPublisher.id.toString() === p.toString(),
      ),
  )
}

/**
 * Gets new pros from contacts that don't exist in current pro list.
 * @param contacts - Array of contacts to check for new pros
 * @param pro - Existing pro list to compare against
 * @returns Array of new pros that need to be added
 */
const getNewPros = (contacts: Contact[], pro: Pro[]): Pro[] => {
  return flatten(contacts.map(c => get(c, 'pendingChanges.pros', []))).filter(
    //TODO: review toString() comparision
    p => !pro.some(existingPro => existingPro.id.toString() === p.toString()),
  )
}

/**
 * Synchronizes new publishers and pros by adding them to the server.
 * @param newPublishers - Array of new publishers to add
 * @param newPro - Array of new pros to add
 * @returns Thunk that adds new publishers and pros via API calls
 */
export const syncContactPublishersAndPro = (
  newPublishers: Publisher[],
  newPro: Pro[],
): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    const proPromise = Promise.all(
      newPro.map(p => api.addPro(p.name, p.external_identifiers)),
    )
    const publisherPromise = Promise.all(
      newPublishers.map(p => api.addPublisher(p.name, p.external_identifiers)),
    )
    const [proResults, publisherResults] = await Promise.all([
      proPromise,
      publisherPromise,
    ])
    if (proResults && proResults.length) dispatch(addPro(proResults))
    if (publisherResults && publisherResults.length)
      dispatch(addPublishers(publisherResults))
  }
}
/**
 * Finds ID of existing publisher or pro item by comparing request string.
 * @param requestString - Publisher or Pro to find ID for
 * @param existingItems - Array of existing publishers or pros to search in
 * @returns ID of matching item or null if not found
 */
export const findIdOfExistingItem = (
  requestString: Publisher | Pro,
  existingItems: (Publisher | Pro)[],
): ContactId | null | undefined => {
  const currentItem = existingItems.find(
    existingItem => existingItem.id.toString() === requestString.toString(),
  )

  if (!currentItem) {
    return existingItems.find(
      existingItem => existingItem.name === requestString.toString(),
    )?.id
  }

  return currentItem.id
}

/**
 * Fixes a list of items by finding their corresponding IDs in existing items.
 * @param list - Array of publishers or pros to fix
 * @param existingItems - Array of existing items to match against
 * @returns Array of IDs for matching items
 */
const fixItemsList = (
  list: (Publisher | Pro)[],
  existingItems: (Publisher | Pro)[], // TODO: update return value type > ContactId[]
): any[] =>
  list.map(item => findIdOfExistingItem(item, existingItems)).filter(Boolean)

/**
 * Fixes pending changes in contacts by resolving publisher and pro references.
 * @param contacts - Array of contacts with pending changes
 * @param pros - Array of available pros
 * @param publishers - Array of available publishers
 * @returns Array of contacts with resolved pending changes
 */
const fixContactPendingChanges = (
  contacts: Contact[],
  pros: Pro[],
  publishers: Publisher[],
) => {
  return contacts.map(c => {
    const contactProList = get(c, 'pendingChanges.pros', null)
    const contactPublisherList = get(c, 'pendingChanges.publishers', null)
    if (!c.pendingChanges) return c
    const fixedProList = contactProList
      ? fixItemsList(contactProList, pros)
      : undefined
    const fixedPublishersList = contactPublisherList
      ? fixItemsList(contactPublisherList, publishers)
      : undefined
    const result = { ...c, pendingChanges: { ...c.pendingChanges } }
    if (fixedProList) result.pendingChanges.pros = fixedProList
    if (fixedPublishersList)
      result.pendingChanges.publishers = fixedPublishersList
    return result
  })
}

/**
 * Transforms a contact with pending changes into final contact object.
 * @param c - Partial contact object with pending changes
 * @param overrideChanges - Optional object to override specific changes
 * @returns Transformed contact object with applied changes
 */
export const transformPendingContact = (
  c: Partial<Contact>,
  overrideChanges?: object,
): Partial<Contact> => {
  const pendingTags = get(c, 'pendingChanges.tags', c.tags)
  const resetWriterFields =
    pendingTags &&
    !areTagsIncludeValues(pendingTags, ['Writer', 'Artist'], 'any')
  const pros = get(c, 'pendingChanges.pros', c.pros || [])
  const publishers = get(c, 'pendingChanges.publishers', c.publishers || [])
  const alternativeProValue =
    c.default_pro && pros.includes(c.default_pro)
      ? c.default_pro
      : pros[0] || null
  const defaultPro = get(c, 'pendingChanges.default_pro', alternativeProValue)
  const alternativePublisherValue =
    c.default_publisher && publishers.includes(c.default_publisher)
      ? c.default_publisher
      : publishers[0] || null
  const defaultPublisher = get(
    c,
    'pendingChanges.default_publisher',
    alternativePublisherValue,
  )
  const updatedContact = {
    ...c,
    ...c.pendingChanges,
    default_pro: defaultPro ?? undefined,
    default_publisher: defaultPublisher ?? undefined,
    ...overrideChanges,
  }

  if (resetWriterFields) {
    updatedContact.acts = null
    updatedContact.pros = []
    updatedContact.publishers = []
  }

  return updatedContact
}

/**
 * Updates multiple contacts with pending changes and optional notifications.
 * @param contacts - Array of contacts to update
 * @param overrideChanges - Optional changes to override pending contact changes
 * @param pushNotification - Whether to show success/error notifications
 * @returns Thunk that updates contacts with publisher/pro sync and error handling
 */
export const updateContacts = (
  contacts: Contact[],
  overrideChanges: any = null,
  pushNotification: boolean = true, // TODO: update type
): any => {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: CONTACTS_UPDATE,
    })

    try {
      const newPublishers = getNewPublishers(
        contacts,
        getState().entities.publishers,
      )
      const newPros = getNewPros(contacts, getState().entities.pro)
      // sync call modifies pro and publisher objects in reducers, get
      // them after call
      await dispatch(syncContactPublishersAndPro(newPublishers, newPros))
      const {
        entities: { pro, publishers },
      } = getState()
      const modifiedContacts = fixContactPendingChanges(
        contacts,
        pro,
        publishers,
      ).map(c => transformPendingContact(c, overrideChanges))

      // entity_details is the source of truth for these defaults. Do not
      // send the legacy top-level values as well, since they can overwrite
      // the values in entity_details on the API.
      modifiedContacts.forEach(contact => {
        if (contact.pendingChanges?.entity_details) {
          delete contact.default_pro
          delete contact.default_publisher
        }
      })
      await api.updateContacts(modifiedContacts)
      const message =
        contacts.length === 1
          ? `Updated ${contacts.length} contact`
          : `Updated ${contacts.length} contacts`
      if (pushNotification)
        pushUINotification({
          message,
          level: 'success',
        })
      return dispatch({
        type: CONTACTS_UPDATE_SUCCESS,
        contacts: modifiedContacts,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_UPDATE_FAIL,
        error,
      })
      let message
      let errorField
      let errorMessage = ''

      if (Array.isArray(error.body)) {
        errorField = error.body.find(item => item.errors)

        if (errorField?.errors?.email) {
          errorMessage = errorField.errors.email[0]
        }
      } else {
        errorMessage = parseServerError(error?.body || error)
      }

      if (errorMessage) {
        message = `Sorry, that update didn’t work. ${maybeAddPeriod(
          errorMessage,
        )}`
      } else if (contacts.length) {
        // fallback error message
        message =
          'Sorry, that update didn’t work. Please check you’ve filled in all the required fields.'
      }

      if (message) {
        pushUINotification({
          message,
          level: 'error',
        })
      }

      throw error
    }
  }
}
/**
 * Updates tags for multiple contacts.
 * @param contacts - Array of contacts to update tags for
 * @param tags - Optional array of tag strings to apply
 * @returns Thunk that updates contact tags and handles success/error states
 */
export const updateContactsTags = (
  contacts: Contact[],
  tags?: string[] | null | undefined,
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACTS_UPDATE_TAGS,
    })

    try {
      const modifiedContacts = contacts.map(c => ({ ...c, tags }))
      api.updateContacts(modifiedContacts)
      return dispatch({
        type: CONTACTS_UPDATE_TAGS_SUCCESS,
        contacts: modifiedContacts,
      })
    } catch (error) {
      dispatch({
        type: CONTACTS_UPDATE_TAGS_FAIL,
        error,
      })
      throw error
    }
  }
}
/**
 * Deletes multiple contacts by their IDs.
 * @param ids - Array of contact IDs to delete
 * @returns Thunk that deletes contacts and shows success notification with analytics
 */
export const deleteContacts = (
  ids: ContactId[],
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: CONTACTS_DELETE,
      ids,
    })

    try {
      await api.deleteContacts(ids)
      const message =
        ids.length === 1
          ? `Deleted ${ids.length} contact`
          : `Deleted ${ids.length} contacts`
      pushUINotification({
        message,
        level: 'success',
      })
      return dispatch({
        type: CONTACTS_DELETE_SUCCESS,
        ids,
        meta: {
          mixpanel: {
            event: ids.length === 1 ? 'Delete contact' : 'Delete contacts',
            props: {
              Count: ids.length,
            },
          },
        },
      })
    } catch (error) {
      return dispatch({
        type: CONTACTS_DELETE_FAIL,
        ids,
        error,
      })
    }
  }
}
/**
 * Unsubscribes contacts from a specific list or globally.
 * @param contactIds - Array of contact IDs to unsubscribe
 * @param listId - Optional list ID to unsubscribe from, null for global unsubscribe
 * @returns Thunk that unsubscribes contacts and shows success/error notifications
 */
export const unsubscribeContacts = (
  contactIds: number[],
  listId: number | null | undefined,
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    const contactWord = contactIds.length === 1 ? 'user' : 'users'

    try {
      await api.unsubscribeContacts(
        contactIds.map(contactId => ({
          id: contactId,
          saved_recipients_id: listId, // if null it's global unsubscribe
        })),
      )
      dispatch({
        type: CONTACTS_UNSUBSCRIBE_SUCCESS,
        ids: contactIds,
        listId,
      })
      pushUINotification({
        message: `Unsubscribed ${contactWord}!`,
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: `Could not unsubscribe ${contactWord}! Please try again or contact us.`,
        level: 'error',
      })
      return dispatch({
        type: CONTACTS_UNSUBSCRIBE_FAIL,
        error,
      })
    }
  }
}
/**
 * Changes a field value for a specific contact.
 * @param id - ID of the contact to update
 * @param field - Field name to change
 * @param value - New value for the field
 * @returns Action object with contact ID, field, and new value
 */
export const changeContactValue = (
  id: ContactId,
  field: string,
  value: string | string[],
): {
  field: string
  id: ContactId
  type: string
  value: string | Array<string>
} => ({
  type: CONTACTS_VALUE_CHANGE,
  id,
  field,
  value,
})
/**
 * Adds a value to a field for a specific contact.
 * @param id - ID of the contact to update
 * @param field - Field name to add value to
 * @param value - Value to add to the field
 * @returns Action object with contact ID, field, and value to add
 */
export const addContactValue = (
  id: ContactId,
  field: string,
  value: string | string[],
): {
  field: string
  id: ContactId
  type: string
  value: string | Array<string>
} => ({
  type: CONTACTS_VALUE_ADD,
  id,
  field,
  value,
})
/**
 * Removes a value from a field for a specific contact.
 * @param id - ID of the contact to update
 * @param field - Field name to remove value from
 * @param value - Value to remove from the field
 * @returns Action object with contact ID, field, and value to remove
 */
export const removeContactValue = (
  id: ContactId,
  field: string,
  value: string | string[],
): {
  field: string
  id: ContactId
  type: string
  value: string | Array<string>
} => ({
  type: CONTACTS_VALUE_REMOVE,
  id,
  field,
  value,
})
/**
 * Cancels editing mode for contacts.
 * @returns Action object to cancel contact editing
 */
export const cancelContactsEditing = (): {
  type: string
} => ({
  type: CONTACTS_CANCEL_EDITING,
})
/**
 * Toggles selection state for multiple contacts.
 * @param ids - Array of contact IDs to toggle
 * @param selectAllCall - Optional flag indicating this is a select all operation
 * @param deselectAll - Optional flag indicating this is a deselect all operation
 * @returns Action object with contact IDs and selection flags
 */
export const toggleContacts = (
  ids: ContactId[],
  selectAllCall?: boolean,
  deselectAll?: boolean,
): {
  deselectAll: boolean
  ids: Array<ContactId>
  selectAllCall: boolean
  type: string
} => ({
  type: CONTACTS_TOGGLE,
  ids,
  selectAllCall: selectAllCall ?? false,
  deselectAll: deselectAll ?? false,
})
/**
 * Downloads contacts as an export file.
 * @param ids - Optional array of contact IDs to export, exports all if not provided
 * @returns Thunk that exports contacts and handles errors with notifications
 */
export const downloadContacts = (
  ids?: number[],
): ((dispatch: any) =>
  | Promise<any>
  | Promise<{
      error: boolean
    }>) => {
  return async (_dispatch: (...args: Array<any>) => any) => {
    try {
      const response = await api.exportContacts(ids)
      return response
    } catch (_error) {
      pushUINotification({
        message: 'List export error!',
        level: 'error',
      })
      return {
        error: true,
      }
    }
  }
}
